/**
 * Sharing a map with a link (Alex, 2026-10-28; docs/plans/
 * 2026-10-28-link-sharing.md, "Redesign": the link is the access), for the
 * interfaces (engine/index.js `sharing.link`). The owner makes it, copies it
 * again, changes the role it gives, turns it off or replaces it (both change
 * the map key: whoever has it open is cut off at once). Whoever holds it
 * opens the map without becoming a member: kept in their maps while the link
 * works (signed in), or for this page only.
 *
 * End to end: the link's secret (after '#') never reaches the server. The
 * owner and admins keep it on the server encrypted under the map key, to copy
 * the link again. Opening a link walks the map's key log as the link, from
 * its entry on, anchored on the owner key the link itself names
 * (map-key-trust.js); key changes are followed the same way. The server
 * knows who holds the link by a token derived from the secret: the sync
 * socket opens with a one-time ticket asked with it (websocket.js), the
 * attachments with it in a header (`linkHeader`).
 */
import * as MapKeyTrust from "../crypto/map-key-trust.js";
import { VaultStorage } from "../crypto/vault-storage.js";
import { Keypair } from "../crypto/keypair.js";
import { linkAddress, parseLinkAddress, decryptLinkSecret } from "../crypto/link-keys.js";
import { rotateShareDEK } from "./share-rotation.js";
import { getServerOrigin } from "../storage/index.js";
import { emit, Events } from "../core/events.js";
import { refusal } from "./link-access.js";
import { t } from "../../shared/i18n/index.js";

const headers = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" };

/** The server the link points to (the desktop app's own address would not open anywhere). */
function serverOrigin() {
  return getServerOrigin?.() || globalThis.location?.origin || "";
}

/**
 * The map's link, or null: { id, role, public_key, created_at, created_by,
 * encrypted_secret? (owner and admins) }.
 */
export async function get(mapId) {
  const response = await fetch(`/api/shares/${mapId}/link`, { credentials: "include" });
  if (response.status === 403 || response.status === 404) return null; // not shared with us, or no server support
  if (!response.ok) throw await refusal(response, "share.link.error.loadFailed");
  return response.json();
}

/**
 * The key of who made the link (the owner; the link names it), from the
 * map's verified key log. Null when the server no longer says who: a new
 * link is needed.
 */
async function creatorKeyOf(mapId, link) {
  if (!link.created_by) return null;
  const state = await MapKeyTrust.ensure(mapId);
  return state?.witnesses.find((w) => w.userId === link.created_by)?.publicKey || null;
}

/** The link to share again (owner and admins), or null when the map has none (or it needs renewing). */
export async function address(mapId, link = null) {
  link ||= await get(mapId);
  if (!link?.encrypted_secret) return null;
  const creatorKey = await creatorKeyOf(mapId, link);
  if (!creatorKey) return null;
  const dek = await VaultStorage.getDocDEK(mapId);
  let secret;
  try {
    secret = await decryptLinkSecret(dek, mapId, link.id, link.encrypted_secret);
  } finally {
    dek.fill(0);
  }
  try {
    return linkAddress(serverOrigin(), link.id, secret, creatorKey);
  } finally {
    secret.fill(0);
  }
}

/** The owner: make the map's link ('viewer' or 'editor'). Resolves with its address. */
export async function create(mapId, role) {
  const prepared = await MapKeyTrust.prepareLink(mapId, role);
  try {
    const response = await fetch(`/api/shares/${mapId}/link`, {
      method: "POST", credentials: "include", headers, body: JSON.stringify(prepared.body),
    });
    if (!response.ok) throw await refusal(response, "share.link.error.createFailed");
    const entry = VaultStorage.getDoc(mapId);
    if (entry?.wrappedDEK) await VaultStorage.updateDocWrappedDEK(mapId, entry.wrappedDEK, { verifiedAuthority: prepared.event.state });
    emit(Events.SHARE_LINK_CHANGED, { docId: mapId });
    return linkAddress(serverOrigin(), prepared.linkId, prepared.secret, prepared.creatorPublicKey);
  } finally {
    prepared.secret.fill(0);
  }
}

/** The owner: the role the link gives (maps open by it get it at once). */
export async function setRole(mapId, role) {
  const link = await get(mapId);
  if (!link) throw new Error(t("share.link.error.gone"));
  if (link.role === role) return;
  const event = await MapKeyTrust.prepareLinkRole(mapId, link.id, role);
  const response = await fetch(`/api/shares/${mapId}/link`, {
    method: "PATCH", credentials: "include", headers, body: JSON.stringify({ role, key_authority: event }),
  });
  if (!response.ok) throw await refusal(response, "share.link.error.changeFailed");
  const entry = VaultStorage.getDoc(mapId);
  if (entry?.wrappedDEK) await VaultStorage.updateDocWrappedDEK(mapId, entry.wrappedDEK, { verifiedAuthority: event.state });
  emit(Events.SHARE_LINK_CHANGED, { docId: mapId });
}

/**
 * The owner: turn the link off. The map key changes (like removing
 * someone): the link's secret opens nothing new, and whoever has the map
 * open by it is cut off. `progress`: { report, signal, setCancellable }
 * (share-rotation.js).
 */
export async function turnOff(mapId, progress = {}) {
  await rotateShareDEK(mapId, { ...progress, turnOffLink: true });
  emit(Events.SHARE_LINK_CHANGED, { docId: mapId });
}

/** The owner: a new link replacing the old one (which stops working). Resolves with its address. */
export async function renew(mapId, role, progress = {}) {
  await turnOff(mapId, progress);
  return create(mapId, role);
}

/** Whether `text` is a link to a map (the whole address or what follows '#'). */
export function isLink(text) {
  return !!parseLinkAddress(text);
}

// Whoever holds a link: link-access.js.
export { open, isLinkMap, isGone, held, remove } from "./link-access.js";
